// packages/shared/src/client/components/UserMenu.tsx
// Pastille utilisateur du header → dropdown (avatar + nom, "Mon profil",
// "Se déconnecter"). Lit le store auth → réagit au changement d'avatar.
// Partagé par les apps prompts / academy / hub.
import { AppIcon, cn, useClickOutside, IconUser, IconLogout, IconChevronDown } from '@neuroforge/ui'
import { useRef, useState } from 'react'
import { Link } from 'react-router'

import { logout } from '../auth'
import { useAuthStore } from '../stores/auth.store'

export interface UserMenuProps {
  /** Chemin de la page profil (def. /profile). */
  profilePath?: string
  className?: string
}

function Avatar({
  avatarUrl,
  initial,
  size,
}: {
  avatarUrl?: string | null
  initial: string
  size: 'sm' | 'lg'
}) {
  const box = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10'
  const text = size === 'sm' ? 'text-[11px]' : 'text-sm'
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className={cn(box, 'rounded-full object-cover')} />
  }
  return (
    <div
      className={cn(
        box,
        'flex items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600',
      )}
    >
      <span className={cn(text, 'font-black text-white')}>{initial}</span>
    </div>
  )
}

export function UserMenu({ profilePath = '/profile', className }: UserMenuProps) {
  const user = useAuthStore((s) => s.user)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false))

  if (!user) return null

  const initial = user.displayName?.[0]?.toUpperCase() ?? user.email?.[0]?.toUpperCase() ?? 'U'
  const name = user.displayName ?? user.email

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Menu du compte"
        className="glass flex items-center gap-2.5 rounded-full px-3 py-2 transition hover:border-[var(--accent)]"
      >
        <Avatar avatarUrl={user.avatarUrl} initial={initial} size="sm" />
        <span className="text-(--muted) hidden max-w-[120px] truncate text-xs font-semibold sm:block">
          {name}
        </span>
        <AppIcon
          icon={IconChevronDown}
          size={16}
          className={cn('text-(--muted) transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="menu"
          className="glass animate-slide-down absolute right-0 top-[calc(100%+8px)] z-50 w-80 rounded-2xl p-2 shadow-xl"
        >
          <div className="border-(--border) flex items-center gap-3 border-b px-3 pb-2.5">
            <Avatar avatarUrl={user.avatarUrl} initial={initial} size="sm" />
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{name}</p>
              <p className="text-(--muted) truncate text-xs">{user.email}</p>
            </div>
          </div>

          <Link
            to={profilePath}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="hover:bg-(--bg-elevated) mt-1 flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-semibold transition"
          >
            <AppIcon icon={IconUser} size={16} className="text-(--accent)" aria-hidden />
            Mon profil
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              void logout()
            }}
            className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-semibold text-red-500 transition hover:bg-red-500/5"
          >
            <AppIcon icon={IconLogout} size={16} aria-hidden />
            Se déconnecter
          </button>
        </div>
      )}
    </div>
  )
}
